import { Injectable, Injector } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, Observable, catchError, throwError, of } from "rxjs";
import { tap, map, finalize, shareReplay } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { AuditService } from "./audit.service";
import {
  ApiResponse,
  AuthData,
  LoginRequest,
  RegisterRequest,
  User,
} from "../models";

/** Re-export auth models for backward-compatible imports from this service. */
export type { AuthData, LoginRequest, RegisterRequest, User } from "../models";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly API_URL = environment.apiBaseUrl; // Use environment API URL
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  private refreshInFlight$: Observable<string> | null = null;

  public currentUser$ = this.currentUserSubject.asObservable();
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();

  private _auditService?: AuditService;
  private get auditService(): AuditService {
    if (!this._auditService) {
      this._auditService = this.injector.get(AuditService);
    }
    return this._auditService;
  }

  constructor(private http: HttpClient, private injector: Injector) {
    // Check if user is already logged in on service initialization
    this.checkAuthStatus();
  }

  private checkAuthStatus(): void {
    const token = localStorage.getItem("authToken");
    const refreshToken = localStorage.getItem("refreshToken");
    const user = localStorage.getItem("currentUser");
    const expiry = localStorage.getItem("tokenExpiry");

    if (!user) {
      return;
    }

    if (token && expiry && new Date(expiry) > new Date()) {
      this.currentUserSubject.next(JSON.parse(user));
      this.isLoggedInSubject.next(true);
      return;
    }

    if (refreshToken) {
      // Restore persisted session state only. Startup/session recovery is handled
      // by APP_INITIALIZER, guards, and the auth interceptor to avoid refresh races.
      this.currentUserSubject.next(JSON.parse(user));
      this.isLoggedInSubject.next(true);
      return;
    }

    // Access expired and no refresh token
    if (token || expiry) {
      this.clearLocalSession();
    }
  }

  login(credentials: LoginRequest): Observable<ApiResponse<AuthData>> {
    return this.http
      .post<ApiResponse<AuthData>>(`${this.API_URL}/auth/login`, credentials)
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.setAuthData(response.data);
            // Login success is recorded server-side with X-Correlation-Id (see AuditCorrelationInterceptor).
          }
        }),
        catchError((err) => {
          // Login failures are recorded server-side (includes reason like invalid_password) and linked via X-Correlation-Id.
          const msg = this.extractErrorMessage(err, "Login failed. Please try again.");
          const error = new Error(msg) as Error & { status?: number; error?: unknown };
          error.status = err?.status;
          error.error = err?.error;
          return throwError(() => error);
        })
      );
  }

  register(userData: RegisterRequest): Observable<ApiResponse<AuthData>> {
    // Audit submit (never include password).
    this.auditService.trackAction(
      "ACTION_ACCOUNT_CREATE_SUBMIT",
      "account_create_submit",
      {
        input: {
          email: userData?.email ?? null,
          username: userData?.username ?? null,
          firstName: userData?.firstName ?? null,
          lastName: userData?.lastName ?? null,
          phone: userData?.phone ?? null,
          city: userData?.city ?? null,
          state: userData?.state ?? null,
          zipCode: userData?.zipCode ?? null,
          courtCaseNumber: userData?.courtCaseNumber ?? null,
          driversLicenseNumber: userData?.driversLicenseNumber ?? null,
          dateOfBirth: userData?.dateOfBirth ?? null,
          smsEnabled: userData?.smsEnabled ?? null,
        },
      }
    );

    return this.http
      .post<ApiResponse<AuthData>>(`${this.API_URL}/auth/register`, userData)
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.setAuthData(response.data);
            this.auditService.trackAction(
              "ACTION_ACCOUNT_CREATE_RESULT",
              "account_create_success",
              { success: true },
              true
            );
          } else {
            this.auditService.trackAction(
              "ACTION_ACCOUNT_CREATE_RESULT",
              "account_create_failed",
              {
                success: false,
                message: String(response?.message || "").slice(0, 220) || undefined,
              },
              true
            );
          }
        }),
        catchError((err) => {
          const msg = this.extractErrorMessage(err, "Account creation failed");
          this.auditService.trackAction(
            "ACTION_ACCOUNT_CREATE_RESULT",
            "account_create_failed",
            { success: false, message: String(msg).slice(0, 220) },
            true
          );
          const error = new Error(msg) as Error & { status?: number; error?: unknown };
          error.status = err?.status;
          error.error = err?.error;
          return throwError(() => error);
        })
      );
  }

  private extractErrorMessage(error: any, fallback: string): string {
    if (!error) {
      return fallback;
    }
    const body = error.error;
    if (typeof body === "string" && body.trim()) {
      return body;
    }
    if (body?.message && typeof body.message === "string") {
      return body.message;
    }
    if (body?.title && typeof body.title === "string") {
      return body.title;
    }
    if (error.message && !String(error.message).startsWith("Http failure response")) {
      return error.message;
    }
    return fallback;
  }

  // Email verification methods
  sendVerificationEmail(email: string): Promise<any> {
    this.auditService.trackAction(
      "ACTION_EMAIL_VERIFICATION_SEND_SUBMIT",
      "email_verification_send_submit",
      { input: { email } }
    );

    return this.http
      .post(`${this.API_URL}/auth/send-verification`, { email })
      .toPromise()
      .then((res) => {
        this.auditService.trackAction(
          "ACTION_EMAIL_VERIFICATION_SEND_RESULT",
          "email_verification_send_success",
          { result: { success: true }, response: { success: true } },
          true
        );
        return res;
      })
      .catch((error) => {
        // Extract meaningful error message from HTTP response
        let errorMessage = "Failed to send verification email";

        if (error.error) {
          if (typeof error.error === "string") {
            errorMessage = error.error;
          } else if (error.error.message) {
            errorMessage = error.error.message;
          } else if (error.error.error) {
            errorMessage = error.error.error;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        const processedError = new Error(errorMessage);
        (processedError as any).status = error.status;
        (processedError as any).error = error.error;
        (processedError as any).retryAfterSeconds =
          typeof error.error?.retryAfterSeconds === "number"
            ? error.error.retryAfterSeconds
            : undefined;

        this.auditService.trackAction(
          "ACTION_EMAIL_VERIFICATION_SEND_RESULT",
          "email_verification_send_failed",
          {
            result: { success: false },
            ui: { message: String(errorMessage).slice(0, 220) },
          },
          true
        );

        throw processedError;
      });
  }

  verifyEmail(email: string, code: string): Promise<any> {
    this.auditService.trackAction(
      "ACTION_EMAIL_VERIFICATION_CONFIRM_SUBMIT",
      "email_verification_confirm_submit",
      { input: { email, code: String(code || "").trim().slice(0, 12) + (code ? "…" : "") } }
    );

    return this.http
      .post(`${this.API_URL}/auth/verify-email`, { email, code })
      .toPromise()
      .then((res) => {
        this.auditService.trackAction(
          "ACTION_EMAIL_VERIFICATION_CONFIRM_RESULT",
          "email_verification_confirm_success",
          { result: { success: true }, response: { success: true } },
          true
        );
        return res;
      })
      .catch((error) => {
        // Extract meaningful error message from HTTP response
        let errorMessage = "Invalid verification code";

        if (error.error) {
          if (typeof error.error === "string") {
            errorMessage = error.error;
          } else if (error.error.message) {
            errorMessage = error.error.message;
          } else if (error.error.error) {
            errorMessage = error.error.error;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        // Create a proper error object
        const processedError = new Error(errorMessage);
        (processedError as any).status = error.status;
        (processedError as any).error = error.error;
        (processedError as any).attemptsRemaining =
          typeof error.error?.attemptsRemaining === "number"
            ? error.error.attemptsRemaining
            : undefined;
        (processedError as any).codeExhausted = !!error.error?.codeExhausted;

        this.auditService.trackAction(
          "ACTION_EMAIL_VERIFICATION_CONFIRM_RESULT",
          "email_verification_confirm_failed",
          {
            result: { success: false },
            ui: { message: String(errorMessage).slice(0, 220) },
          },
          true
        );

        throw processedError;
      });
  }

  // Profile read/update — customer portal uses /auth/profile (not admin /users/*).
  fetchUserProfile(): Observable<ApiResponse<User>> {
    const headers = this.getAuthHeaders();
    return this.http
      .get<ApiResponse<User>>(`${this.API_URL}/auth/profile`, { headers })
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            localStorage.setItem("currentUser", JSON.stringify(response.data));
            this.currentUserSubject.next(response.data);
          }
        }),
      );
  }

  updateProfile(profileData: any): Observable<ApiResponse<User>> {
    const headers = this.getAuthHeaders();

    const current = this.getCurrentUser() as any;
    const norm = (v: any) =>
      v === null || v === undefined ? "" : String(v).trim();

    const isProfileNewCompletion = (u: any): boolean => {
      if (!u) return true;
      const watch = [
        "firstName",
        "lastName",
        "email",
        "mobile",
        "address",
        "city",
        "state",
        "zipCode",
      ];
      return watch.every((k) => !norm(u[k]));
    };

    const payloadKeys = Object.keys(profileData || {}).filter((k) => k !== "userId");

    const valueChanged = (key: string): boolean => {
      const newRaw = profileData[key];
      const oldRaw = current?.[key];
      if (typeof newRaw === "boolean" || typeof oldRaw === "boolean") {
        return !!newRaw !== !!oldRaw;
      }
      return norm(newRaw) !== norm(oldRaw);
    };

    const isNew = isProfileNewCompletion(current);
    const changedKeysOnly = payloadKeys.filter((k) => valueChanged(k));
    const keysToAudit = isNew ? payloadKeys : changedKeysOnly;

    const maskValue = (key: string, value: any) => {
      if (value === null || value === undefined) return value;
      const k = key.toLowerCase().replace(/[_\s-]/g, "");
      // Only redact credentials and payment PAN/CVV/expiry — profile contact fields stay readable in audit.
      if (k.includes("password")) return "***";
      if (k === "cvv" || k === "cvc" || k === "cvv2" || k.endsWith("cvv")) return "***";
      if (
        k === "cardnumber" ||
        k === "cardnum" ||
        k === "pan" ||
        k === "primaryaccountnumber"
      ) {
        return "***";
      }
      if (
        k === "expirationdate" ||
        k === "expiration" ||
        k === "expiry" ||
        k === "expdate" ||
        k === "cardexpiration" ||
        k === "cardexp" ||
        (k.includes("expiration") && k.includes("card")) ||
        (k.includes("expiry") && k.includes("card"))
      ) {
        return "***";
      }
      return String(value);
    };

    const entered: any = {};
    const previous: any = {};
    for (const k of keysToAudit) {
      entered[k] = maskValue(k, profileData?.[k]);
      previous[k] = maskValue(k, current?.[k]);
    }

    const uiSubmitMessage = "Profile update submitted";
    this.auditService.trackAction(
      "ACTION_PROFILE_UPDATE_SUBMIT",
      "profile_update_submit",
      {
        input: {
          profileChangeKind: isNew ? "new" : "update",
          changedKeys: keysToAudit,
          previousValues: previous,
          values: entered,
          hadCurrentUser: !!current,
        },
        ui: { message: uiSubmitMessage },
      }
    );

    return this.http
      .put<ApiResponse<User>>(`${this.API_URL}/auth/profile`, profileData, {
        headers,
      })
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            // Update current user in local storage and state with the response data
            localStorage.setItem("currentUser", JSON.stringify(response.data));
            this.currentUserSubject.next(response.data);
          }

          this.auditService.trackAction(
            "ACTION_PROFILE_UPDATE_RESULT",
            response.success ? "profile_update_success" : "profile_update_failed",
            {
              input: {
                profileChangeKind: isNew ? "new" : "update",
                changedKeys: keysToAudit,
                previousValues: previous,
                values: entered,
              },
              result: { success: !!response.success },
              ui: { message: String(response.message || "").slice(0, 180) || undefined },
              response: { success: !!response.success },
            },
            true
          );
        })
        ,
        catchError((err) => {
          const msg =
            err?.error?.message ||
            err?.error?.title ||
            (typeof err?.error === "string" ? err.error : "") ||
            err?.message ||
            "Profile update failed";
          this.auditService.trackAction(
            "ACTION_PROFILE_UPDATE_RESULT",
            "profile_update_failed",
            {
              input: {
                profileChangeKind: isNew ? "new" : "update",
                changedKeys: keysToAudit,
                previousValues: previous,
                values: entered,
              },
              result: { success: false },
              ui: { message: String(msg).slice(0, 180) },
            },
            true
          );
          return throwError(() => err);
        })
      );
  }

  // Change password method
  changePassword(passwordData: {
    currentPassword: string;
    newPassword: string;
    confirmPassword?: string;
  }): Promise<any> {
    const headers = this.getAuthHeaders();

    this.auditService.trackAction(
      "ACTION_PASSWORD_CHANGE_SUBMIT",
      "password_change_submit",
      { input: { hasCurrentPassword: !!passwordData?.currentPassword, hasNewPassword: !!passwordData?.newPassword } }
    );

    const payload = {
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
      confirmPassword:
        passwordData.confirmPassword ?? passwordData.newPassword,
    };

    return this.http
      .post(`${this.API_URL}/auth/change-password`, payload, { headers })
      .toPromise()
      .then((res) => {
        this.auditService.trackAction(
          "ACTION_PASSWORD_CHANGE_RESULT",
          "password_change_success",
          { result: { success: true }, response: { success: true } },
          true
        );
        return res;
      })
      .catch((error) => {
        const msg =
          error?.error?.message ||
          error?.error?.title ||
          (typeof error?.error === "string" ? error.error : "") ||
          error?.message ||
          "Password change failed";
        this.auditService.trackAction(
          "ACTION_PASSWORD_CHANGE_RESULT",
          "password_change_failed",
          { result: { success: false }, ui: { message: String(msg).slice(0, 220) } },
          true
        );
        throw error;
      });
  }

  // Forgot password - request password reset token
  forgotPassword(emailOrUsername: string): Promise<any> {
    this.auditService.trackAction(
      "ACTION_PASSWORD_RESET_REQUEST_SUBMIT",
      "password_reset_request_submit",
      { input: { emailOrUsername: String(emailOrUsername || "").trim().slice(0, 120) } }
    );

    return this.http
      .post(`${this.API_URL}/auth/forgot-password`, { emailOrUsername })
      .toPromise()
      .then((response: any) => {
        // Check if response indicates failure
        if (response && response.success === false) {
          const errorMessage = response.message || "Failed to send password reset email";
          const error = new Error(errorMessage);
          (error as any).status = 400;
          this.auditService.trackAction(
            "ACTION_PASSWORD_RESET_REQUEST_RESULT",
            "password_reset_request_failed",
            { result: { success: false }, ui: { message: String(errorMessage).slice(0, 220) } },
            true
          );
          throw error;
        }
        this.auditService.trackAction(
          "ACTION_PASSWORD_RESET_REQUEST_RESULT",
          "password_reset_request_success",
          { result: { success: true }, response: { success: true } },
          true
        );
        return response;
      })
      .catch((error) => {
        let errorMessage = "Failed to send password reset email";
        if (error.error) {
          if (typeof error.error === "string") {
            errorMessage = error.error;
          } else if (error.error.message) {
            errorMessage = error.error.message;
          } else if (error.error.error) {
            errorMessage = error.error.error;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        const processedError = new Error(errorMessage);
        (processedError as any).status = error.status || 400;

        this.auditService.trackAction(
          "ACTION_PASSWORD_RESET_REQUEST_RESULT",
          "password_reset_request_failed",
          { result: { success: false }, ui: { message: String(errorMessage).slice(0, 220) } },
          true
        );
        throw processedError;
      });
  }

  // Reset password with token
  resetPassword(email: string, token: string, newPassword: string, confirmPassword: string): Promise<any> {
    this.auditService.trackAction(
      "ACTION_PASSWORD_RESET_SUBMIT",
      "password_reset_submit",
      {
        input: {
          email,
          tokenPrefix: String(token || "").trim().slice(0, 6) + (token ? "…" : ""),
          hasNewPassword: !!newPassword,
          hasConfirmPassword: !!confirmPassword,
        },
      }
    );

    return this.http
      .post(`${this.API_URL}/auth/reset-password`, {
        email,
        token,
        newPassword,
        confirmPassword,
      })
      .toPromise()
      .then((res) => {
        this.auditService.trackAction(
          "ACTION_PASSWORD_RESET_RESULT",
          "password_reset_success",
          { result: { success: true }, response: { success: true } },
          true
        );
        return res;
      })
      .catch((error) => {
        let errorMessage = "Failed to reset password";
        const errorDetails: { [key: string]: string[] } = {};

        if (error.error) {
          // Handle validation errors format from .NET API
          if (error.error.errors && typeof error.error.errors === "object") {
            // errors object contains field names as keys and arrays of error messages as values
            Object.keys(error.error.errors).forEach((field) => {
              errorDetails[field] = error.error.errors[field];
            });
            // Use first error message as main message
            const firstFieldErrors = Object.values(errorDetails)[0] as string[];
            if (firstFieldErrors && firstFieldErrors.length > 0) {
              errorMessage = firstFieldErrors[0];
            }
          } else if (typeof error.error === "string") {
            errorMessage = error.error;
          } else if (error.error.message) {
            errorMessage = error.error.message;
          } else if (error.error.error) {
            errorMessage = error.error.error;
          } else if (error.error.title) {
            // RFC 7807 Problem Details format
            errorMessage = error.error.title || "Validation error occurred";
          }
        }

        const processedError = new Error(errorMessage);
        (processedError as any).status = error.status;
        (processedError as any).fieldErrors = errorDetails;

        this.auditService.trackAction(
          "ACTION_PASSWORD_RESET_RESULT",
          "password_reset_failed",
          {
            result: { success: false },
            ui: { message: String(errorMessage).slice(0, 220) },
          },
          true
        );
        throw processedError;
      });
  }

  logout(): void {
    // Write audit before clearing token so it can be sent.
    this.auditService.trackLogout();
    const refreshToken = this.getRefreshToken();
    this.clearLocalSession();

    if (refreshToken) {
      this.http
        .post(`${this.API_URL}/auth/logout`, { refreshToken })
        .pipe(catchError(() => of(null)))
        .subscribe();
    }
  }

  /**
   * Refresh access token using stored refresh token.
   * Single-flight: concurrent callers share one in-flight request.
   * Do not retry — refresh tokens rotate server-side; retrying the old token clears the session.
   */
  refreshAccessToken(): Observable<string> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error("No refresh token available"));
    }

    this.refreshInFlight$ = this.http
      .post<ApiResponse<AuthData>>(`${this.API_URL}/auth/refresh`, { refreshToken })
      .pipe(
        map((response) => {
          if (!response.success || !response.data) {
            throw new Error(response.message || "Token refresh failed");
          }
          this.setAuthData(response.data);
          return response.data.token;
        }),
        catchError((err) => {
          this.clearLocalSession();
          return throwError(() => err);
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay(1)
      );

    return this.refreshInFlight$;
  }

  getRefreshToken(): string | null {
    return localStorage.getItem("refreshToken");
  }

  isTokenExpired(): boolean {
    const expiry = localStorage.getItem("tokenExpiry");
    if (!expiry) {
      return true;
    }
    return new Date() >= new Date(expiry);
  }

  /**
   * Ensures a usable access token before guarded routes load data.
   * If access is expired but a refresh token exists, waits for refresh.
   * Returns false when the session cannot be renewed (caller should send user to login).
   */
  ensureValidSession(): Observable<boolean> {
    const user = this.getCurrentUser() || localStorage.getItem("currentUser");
    if (!user && !this.getRefreshToken()) {
      return of(false);
    }

    if (!this.isTokenExpired() && this.getToken()) {
      return of(true);
    }

    if (!this.getRefreshToken()) {
      this.clearLocalSession();
      return of(false);
    }

    return this.refreshAccessToken().pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  private clearLocalSession(): void {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("tokenExpiry");
    this.currentUserSubject.next(null);
    this.isLoggedInSubject.next(false);
  }

  /**
   * True when the API reports the JWT user id no longer exists (e.g. account removed, wrong DB).
   * Call {@link logout} and redirect to login when this returns true.
   */
  isStaleSessionUserError(error: unknown): boolean {
    const e = error as { status?: number; error?: unknown; message?: string };
    const status = e?.status;
    if (status !== 400 && status !== 404) {
      return false;
    }
    const msg = AuthService.extractHttpErrorBodyText(error).toLowerCase();
    return msg.includes("user with id") && msg.includes("not found");
  }

  private static extractHttpErrorBodyText(error: unknown): string {
    const e = error as { error?: unknown; message?: string };
    if (!e) {
      return "";
    }
    const body = e.error;
    if (typeof body === "string") {
      return body;
    }
    if (body && typeof body === "object") {
      const o = body as Record<string, unknown>;
      if (typeof o["message"] === "string") {
        return o["message"] as string;
      }
      if (typeof o["title"] === "string") {
        return o["title"] as string;
      }
    }
    if (typeof e.message === "string") {
      return e.message;
    }
    return "";
  }

  private setAuthData(authData: AuthData): void {
    localStorage.setItem("authToken", authData.token);
    localStorage.setItem("currentUser", JSON.stringify(authData.user));
    localStorage.setItem("tokenExpiry", authData.expiresAt);
    if (authData.refreshToken) {
      localStorage.setItem("refreshToken", authData.refreshToken);
    } else {
      localStorage.removeItem("refreshToken");
    }
    this.currentUserSubject.next(authData.user);
    this.isLoggedInSubject.next(true);
  }

  getToken(): string | null {
    return localStorage.getItem("authToken");
  }

  private getAuthHeaders(): HttpHeaders {
    const token = this.getToken();
    return new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    });
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  isAuthenticated(): boolean {
    const user = this.getCurrentUser() || localStorage.getItem("currentUser");
    if (!user) {
      return false;
    }

    const token = this.getToken();
    const expiry = localStorage.getItem("tokenExpiry");
    const refreshToken = this.getRefreshToken();

    if (token && expiry && new Date(expiry) > new Date()) {
      return true;
    }

    // Access expired but refresh token can renew the session
    if (refreshToken) {
      return true;
    }

    if (token && expiry && new Date() >= new Date(expiry)) {
      this.auditService.trackTokenExpired();
      this.clearLocalSession();
    }

    return false;
  }

  // Student role check (simplified since we only handle students)
  isStudent(): boolean {
    const user = this.getCurrentUser();
    return user?.role === "student" || user?.role === "admin"; // Admin can also act as student
  }
}
